/**
 * LG Channel Vietnam 접근 규칙 (rule-based).
 *
 * BE가 권한을 3계층으로 분리했고 프론트는 그 계약을 그대로 읽는다.
 *
 * | 계층 | BE 판정 | 프론트 |
 * |---|---|---|
 * | 진입 자격 | `user.is_lg_channel_vietnam` OR `company.is_lg_channel_vietnam` | `accountStore.isLgChannelVietnam()` |
 * | 행위 권한 | role → permission code | `hasPermission(menu, action)` |
 * | 데이터 스코프 | 서버가 company로 강제 (admin role은 조회만 전 CP 우회) | **판정하지 않는다** |
 *
 * AS-IS였던 `platform.id === 3` 판정은 삭제됐다. platform은 테넌트 경계이지 권한 단위가 아니고,
 * VN CP도 ODK platform으로 통합됐다.
 *
 * 데이터 스코프를 프론트가 판정하지 않는 것이 이 설계의 핵심이다. 목록 엔드포인트는 하나이고
 * 서버가 호출자에 따라 자기 company / 전 CP를 결정한다. 따라서 프론트는 "내가 운영진인가"를
 * 알 필요가 없고, 응답에 company가 섞여 오면 그때 Company 컬럼을 노출하면 된다.
 */

/**
 * 사이드바 메뉴 키 = permission code의 콜론 앞부분.
 * `accountStore.getMenus()`가 `code.split(':')[0]`으로 유도하므로 별도 매핑이 필요 없다.
 */
export const VIETNAM_UPLOADS_MENU = 'vietnam-uploads';

/**
 * 업로드 화면(`/assets/add-vn`) 사이드바 메뉴 키.
 *
 * 이 키는 permission code에서 유도되지 않는 **유일한 예외**다. code가
 * `vietnam-uploads:add` 하나라 `split(':')[0]`으로는 목록 메뉴와 구분되지 않기 때문에,
 * `getMenus()`의 VN 분기가 `canAddVietnamUpload()`를 보고 직접 넣는다.
 *
 * VN 전용 계정에만 노출한다 — 그 계정은 Import & Upload 화면(운영진용 진입점)이
 * allowlist 밖이라 볼 수 없으므로 사이드바 메뉴가 유일한 진입로다.
 */
export const VIETNAM_UPLOAD_NEW_MENU = 'vietnam-upload-new';

/** 제한 계정 판정용 prefix (vendor-* 와 동형). */
export const VIETNAM_MENU_PREFIX = 'vietnam-';

/**
 * 이 메뉴의 action. code는 `vietnam-uploads:{action}` 으로 조립된다
 * (BE `authorization` 마이그레이션 0003에서 콜론 표기로 통일).
 *
 * action 어휘는 기존 권한과 같은 `view` / `add` / `edit` / `delete` 를 따른다
 * (예: `import-upload:view`, `import-upload:edit`).
 *
 * - `view`   목록 조회 (`/vietnam-uploads`)
 * - `add`    업로드 화면 (`/assets/add-vn`)
 * - `ingest` 인제스트 실행 — 자기 company 레코드에만. 표준 CRUD 밖의 커스텀 action이다.
 */
export const VIETNAM_UPLOAD_ACTIONS = {
  view: 'view',
  add: 'add',
  ingest: 'ingest',
} as const;

/** 세 code를 묶어 부여하는 BE role 이름. 운영에서 계정에 수동 부여한다. */
export const LG_CHANNEL_VIETNAM_ROLE = 'lg_channel_vietnam';

/**
 * VN 전용 계정이 호출할 수 있는 API prefix. BE `ScopedJWTAuthentication.ALLOWED_PREFIXES`와
 * 같은 값이며, 이 목록 밖은 403이다.
 *
 * 사이드바에 다른 도메인 메뉴를 노출하면 클릭하는 즉시 403이므로,
 * `accountStore.getMenus()`가 VN 전용 계정에게는 account 계열 + VN 메뉴만 남긴다.
 */
export const VIETNAM_ALLOWED_API_PREFIXES = ['/api/v1/vietnam-upload/', '/api/v1/accounts/'];
